import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { RequestAccessView } from '../components/RequestAccessView.js';
import { getAuthSession, hasAccess, isAuthEnabled } from './auth.js';

export type AuthRedirectState = {
  readonly authEnabled: boolean;
  readonly hasSession: boolean;
  readonly pathname: string;
};

const PUBLIC_PATHS = new Set(['/login', '/callback']);

/**
 * Pure decision: should an authenticated-but-unmapped Principal see the
 * fail-closed "request access" screen instead of the app? (FR-018, US3.) True
 * only on a protected route when signed in with NO mapped role — never on the
 * public `/login` / `/callback` routes.
 */
export const shouldShowRequestAccess = (input: {
  readonly authEnabled: boolean;
  readonly hasSession: boolean;
  readonly hasRole: boolean;
  readonly pathname: string;
}): boolean =>
  input.authEnabled && input.hasSession && !input.hasRole && !PUBLIC_PATHS.has(input.pathname);

/**
 * Pure redirect decision for the auth gate. F-009: `/login` is now the
 * Hosted-UI sign-in entry (a "Sign in with Okta" action + post-logout landing),
 * so bouncing an unauthenticated protected route to `/login` IS the bounce to
 * the Hosted-UI sign-in — there is no in-SPA password form any more.
 *
 * `/callback` is PUBLIC: Okta returns the browser there with `?code=…` BEFORE a
 * session exists, and the PKCE exchange runs on that mount. It must never be
 * bounced to `/login` (it self-navigates to `/` once the exchange succeeds), or
 * the whole SSO return path is broken.
 */
export const authRedirectForState = ({
  authEnabled,
  hasSession,
  pathname,
}: AuthRedirectState): '/login' | '/' | undefined => {
  if (!authEnabled) return undefined;
  const isPublicRoute = pathname === '/login' || pathname === '/callback';
  if (!hasSession && !isPublicRoute) return '/login';
  if (hasSession && pathname === '/login') return '/';
  return undefined;
};

export function AuthGate() {
  const location = useLocation();
  const navigate = useNavigate();
  const redirectTo = authRedirectForState({
    authEnabled: isAuthEnabled(),
    hasSession: getAuthSession() !== undefined,
    pathname: location.pathname,
  });

  useEffect(() => {
    if (redirectTo !== undefined) {
      void navigate({ to: redirectTo, replace: true });
    }
  }, [navigate, redirectTo]);

  if (redirectTo !== undefined) return null;

  // Authenticated on a protected route but with no mapped role → fail-closed
  // "request access" screen (FR-018); sign-out stays available within it.
  if (
    shouldShowRequestAccess({
      authEnabled: isAuthEnabled(),
      hasSession: getAuthSession() !== undefined,
      hasRole: hasAccess(),
      pathname: location.pathname,
    })
  ) {
    return <RequestAccessView />;
  }

  return <Outlet />;
}
